// ./src/pages/private/AccountEmailsPage/AccountEmailsPage.tsx
import React, { useState, useCallback, useEffect } from "react";
import { Stack } from "@mantine/core";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { PageProps } from "@sdflc/react-router-pages";

import { DashboardLayout } from "@/components/layouts";
import {
  AppButton,
  AppToolbar,
  ConfirmModal,
  EmptyState,
  SearchInput,
} from "@/components/generic";
import {
  AccountEmailsTable,
  AccountEmailEditModal,
  AccountEmailConfirmModal,
} from "@/components/features";
import { useAccountEmails } from "@/hooks";
import { AccountEmail, AccountEmailInput } from "@/utils/api";
import { useApp } from "@/contexts";
import { DEFAULT_PAGE_SIZE, OPERATION_RIGHTS } from "@/config";
import { ENTITIES } from "@/config/userRoles";

export const AccountEmailsPage: React.FC<PageProps> = (props: PageProps) => {
  const { user, hasRightTo } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<{ id?: string }>();

  // Parse URL to determine which modal to show
  const accountEmailIdFromUrl = params.id;
  const isAddMode = location.pathname.endsWith("/add");
  const isEditMode =
    !!accountEmailIdFromUrl && location.pathname.endsWith("/edit");
  const isConfirmMode =
    !!accountEmailIdFromUrl && location.pathname.endsWith("/confirm");

  const canCreate = hasRightTo(
    ENTITIES.ACCOUNT_EMAILS,
    OPERATION_RIGHTS.CREATE
  );

  // Use the account emails hook for all data management
  const {
    // Data state
    accountEmails,
    loading,
    loadingMore,
    hasMore,
    isEmpty,

    // Search and filters
    filters,
    sortBy,

    // Operations state
    isCreating,
    isUpdating,
    removingId,
    isSendingConfirmation,
    isVerifyingCode,

    // Data operations
    list,
    loadMore,
    refresh,
    get,

    // CRUD operations
    create,
    update,
    remove,

    // Confirmation operations
    sendConfirmationCode,
    verifyCode,

    // Error handling
    lastError,
    clearError,
  } = useAccountEmails({
    autoLoad: true,
    userId: user?.id,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  // Local UI state
  const [emailToDelete, setEmailToDelete] = useState<AccountEmail | null>(null);
  const [searchValue, setSearchValue] = useState<string>("");
  const [editingEmail, setEditingEmail] = useState<AccountEmail | null>(null);
  const [confirmingEmail, setConfirmingEmail] = useState<AccountEmail | null>(
    null
  );
  const [editedEmailId, setEditedEmailId] = useState<string | null>(null);

  // Load email when in edit or confirm mode
  useEffect(() => {
    if ((isEditMode || isConfirmMode) && accountEmailIdFromUrl) {
      const loadEmail = async () => {
        await get(accountEmailIdFromUrl);
      };
      loadEmail();
    }
  }, [isEditMode, isConfirmMode, accountEmailIdFromUrl, get]);

  // Update editing/confirming email when we get data
  useEffect(() => {
    if (accountEmailIdFromUrl) {
      const email = accountEmails.find((e) => e.id === accountEmailIdFromUrl);
      if (email) {
        if (isEditMode) {
          setEditingEmail(email);
        } else if (isConfirmMode) {
          setConfirmingEmail(email);
        }
      }
    } else {
      setEditingEmail(null);
      setConfirmingEmail(null);
    }
  }, [isEditMode, isConfirmMode, accountEmailIdFromUrl, accountEmails]);

  // Handle search input change
  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchValue(value);

      const newFilters: any = { ...filters };
      if (value.trim()) {
        newFilters.searchKeyword = value.trim();
      } else {
        delete newFilters.searchKeyword;
      }

      list(newFilters, { page: 1, pageSize: DEFAULT_PAGE_SIZE }, sortBy, true);
    },
    [filters, sortBy, list]
  );

  // Handle sort change
  const handleSortChange = useCallback(
    (newSortBy: any[]) => {
      list(filters, { page: 1, pageSize: DEFAULT_PAGE_SIZE }, newSortBy, true);
    },
    [filters, list]
  );

  // Handle add button click
  const handleAddEmail = useCallback(() => {
    clearError();
    navigate("/account-emails/add");
  }, [navigate, clearError]);

  // Handle edit email
  const handleEditEmail = useCallback(
    (email: AccountEmail) => {
      clearError();
      if (email.id) {
        navigate(`/account-emails/${email.id}/edit`);
      }
    },
    [navigate, clearError]
  );

  // Handle confirm email
  const handleConfirmEmail = useCallback(
    (email: AccountEmail) => {
      clearError();
      if (email.id) {
        navigate(`/account-emails/${email.id}/confirm`);
      }
    },
    [navigate, clearError]
  );

  // Handle remove email button click
  const handleRemoveEmailClick = useCallback((email: AccountEmail) => {
    setEmailToDelete(email);
  }, []);

  // Handle confirm delete
  const handleConfirmDelete = useCallback(async () => {
    if (emailToDelete?.id) {
      const success = await remove(emailToDelete.id);
      if (success) {
        setEmailToDelete(null);
        // If we're currently editing this email, close the modal
        if (accountEmailIdFromUrl === emailToDelete.id) {
          navigate("/account-emails");
        }
      }
    }
  }, [emailToDelete, remove, accountEmailIdFromUrl, navigate]);

  // Handle cancel delete
  const handleCancelDelete = useCallback(() => {
    setEmailToDelete(null);
  }, []);

  // Handle edit modal close
  const handleEditModalClose = useCallback(() => {
    setEditingEmail(null);
    clearError();
    navigate("/account-emails");
  }, [navigate, clearError]);

  // Handle confirm modal close
  const handleConfirmModalClose = useCallback(() => {
    setConfirmingEmail(null);
    clearError();
    navigate("/account-emails");
  }, [navigate, clearError]);

  // Handle refresh
  const handleRefresh = useCallback(() => {
    refresh();
  }, [refresh]);

  // Handle email creation - redirect to confirmation after success
  const handleCreateEmail = useCallback(
    async (data: AccountEmailInput): Promise<AccountEmail | null> => {
      const result = await create(data);
      if (result?.id) {
        setEditedEmailId(result.id);
        // Redirect to confirmation dialog for newly created email
        navigate(`/account-emails/${result.id}/confirm`);
      }
      return result;
    },
    [create, navigate]
  );

  // Handle email update
  const handleUpdateEmail = useCallback(
    async (
      id: string,
      data: AccountEmailInput
    ): Promise<AccountEmail | null> => {
      const result = await update(id, data);
      if (result) {
        setEditedEmailId(result.id || null);
      }
      return result;
    },
    [update]
  );

  // Handle send confirmation
  const handleSendConfirmation = useCallback(
    async (email: AccountEmail): Promise<boolean> => {
      if (!email.id) return false;
      const result = await sendConfirmationCode(email.id);
      return !!result;
    },
    [sendConfirmationCode]
  );

  // Handle verify code
  const handleVerifyCode = useCallback(
    async (email: AccountEmail, code: string): Promise<boolean> => {
      if (!email.id) return false;
      const result = await verifyCode(email.id, code);
      return !!result;
    },
    [verifyCode]
  );

  return (
    <DashboardLayout {...props}>
      <Stack gap="md">
        {/* Page toolbar with actions and search */}
        <AppToolbar
          preset="page-header"
          sections={[
            {
              id: "actions",
              items: [
                <AppButton
                  key="add-email"
                  iconName="Plus"
                  onClick={handleAddEmail}
                  disabled={isCreating || !!removingId || loading || !canCreate}
                >
                  Add
                </AppButton>,
                <AppButton
                  key="refresh"
                  variant="secondary"
                  iconName="Refresh"
                  onClick={handleRefresh}
                  disabled={isCreating || !!removingId || loading}
                />,
              ],
              align: "left",
              gap: "sm",
            },
            {
              id: "search",
              items: [
                <SearchInput
                  key="search"
                  placeholder="Search emails..."
                  value={searchValue}
                  onChange={(value) => handleSearchChange(value)}
                  style={{ minWidth: 250 }}
                />,
              ],
              align: "right",
            },
          ]}
        />

        {/* Account emails table */}
        <AccountEmailsTable
          accountEmails={accountEmails}
          loading={loading}
          loadingMore={loadingMore}
          hasMore={hasMore}
          sortBy={sortBy}
          onSortChange={handleSortChange}
          onLoadMore={loadMore}
          onEditAccountEmail={handleEditEmail}
          onConfirmAccountEmail={handleConfirmEmail}
          onRemoveAccountEmail={handleRemoveEmailClick}
          removingAccountEmailId={removingId}
          editedItemId={editedEmailId}
        />

        {/* Empty state when no emails */}
        <EmptyState
          display={isEmpty && !loading}
          title="No account emails yet"
          description="Add email addresses that can be used for form notifications and workflow actions"
          actionIcon="Plus"
          actionLabel="Add Email"
          onAction={handleAddEmail}
        />
      </Stack>

      {/* Email edit modal for create/edit */}
      <AccountEmailEditModal
        opened={isAddMode || isEditMode}
        onClose={handleEditModalClose}
        accountEmail={isEditMode ? editingEmail : null}
        onCreateAccountEmail={handleCreateEmail}
        onUpdateAccountEmail={handleUpdateEmail}
        isCreating={isCreating}
        isUpdating={isUpdating}
        lastError={lastError}
        onClearError={clearError}
      />

      {/* Email confirmation modal */}
      <AccountEmailConfirmModal
        opened={isConfirmMode}
        onClose={handleConfirmModalClose}
        accountEmail={confirmingEmail}
        onSendConfirmation={handleSendConfirmation}
        onVerifyCode={handleVerifyCode}
        isSending={isSendingConfirmation}
        isVerifying={isVerifyingCode}
        lastError={lastError}
        onClearError={clearError}
      />

      {/* Delete confirmation modal */}
      <ConfirmModal
        opened={!!emailToDelete}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title="Delete Account Email"
        message={`Are you sure you want to delete "${emailToDelete?.email}"?`}
        details="This action cannot be undone. The email will be removed from all workflows using it."
        confirmInProgress={removingId === emailToDelete?.id}
        confirmLabel="Delete"
        type="danger"
      />
    </DashboardLayout>
  );
};
